import { Vector2, Vector3 } from 'three';
import { DEFAULT_TOLERANCE } from '@/domain/geometry/approxEquals';
import { type LpsCoordinateSystem, type LpsVectors } from '@/domain/anatomy/LPS';
import { degToRad } from '@/util/math';
import { angleFromPlane, angleInPlanarProjection } from '@/domain/geometry/angle';
import { areAcute } from '@/domain/geometry/vector3';
import {
    type AnteversionInclination,
    toDegrees,
    toRadians,
} from '@/domain/anatomy/pelvis/anteversionInclination';

/** The system in which acetabular angles are defined */
export type AcetabularAngleSystem = 'anatomic' | 'radiographic';

/** A set of acetabular angles represented in a particular system */
export type AcetabularAngles<System extends AcetabularAngleSystem = AcetabularAngleSystem> = {
    /** System in which the angles are defined */
    system: System;

    /** Anteversion angle */
    anteversion: number;

    /** Inclination angle */
    inclination: number;
};

/** Create a pair of acetabular angles in anatomic convention */
export function anatomicAngles(angles: AnteversionInclination): AcetabularAngles<'anatomic'> {
    return {
        system: 'anatomic',
        ...angles,
    };
}

/** Convert a pair of acetabular angles to anatomic convention */
export function toAnatomic(angles: AcetabularAngles<'radiographic'>): AcetabularAngles<'anatomic'> {
    const transverse = Math.cos(angles.anteversion) * Math.sin(angles.inclination);
    const superiorInferior = Math.cos(angles.anteversion) * Math.cos(angles.inclination);

    return {
        system: 'anatomic',
        anteversion: Math.atan2(Math.sin(angles.anteversion), transverse),
        inclination: Math.acos(superiorInferior),
    };
}

/** Create a pair of acetabular angles in radiographic convention */
export function radiographicAngles(
    angles: AnteversionInclination,
): AcetabularAngles<'radiographic'> {
    return {
        system: 'radiographic',
        ...angles,
    };
}

/** Convert a pair of acetabular angles to radiographic convention */
export function toRadiographic(
    angles: AcetabularAngles<'anatomic'>,
): AcetabularAngles<'radiographic'> {
    const transverse = Math.cos(angles.anteversion) * Math.sin(angles.inclination);
    const posteriorAnterior = Math.sin(angles.anteversion) * Math.sin(angles.inclination);
    const superiorInferior = Math.cos(angles.inclination);

    const calculateInclination = (): number => {
        if (
            Math.abs(transverse) > DEFAULT_TOLERANCE ||
            Math.abs(superiorInferior) > DEFAULT_TOLERANCE
        ) {
            return Math.atan2(transverse, superiorInferior);
        } else {
            // Degenerate case: in posterior or anterior direction. Fallback is 90 degrees.
            return Math.PI / 2;
        }
    };

    return {
        system: 'radiographic',
        anteversion:
            Math.sign(posteriorAnterior) *
            Math.acos(new Vector2(transverse, superiorInferior).length()),
        inclination: calculateInclination(),
    };
}

/**
 * Calculate radiographic anteversion angle from an 'acetabular vector'. This is the angle between the
 * 'acetabular vector' and the coronal plane.
 * For both cups and acetabular planes the 'acetabular vector' points laterally (away from the pelvis).
 *
 * Definition of Radiographic angles (Murray 1992):
 * The Radiographic Inclination (RI) is defined as the angle between the longitudinal axis and the acetabular
 *      axis when this is projected onto the coronal plane.
 */
export function radiographicAnteversion(acetabularVector: Vector3, vectors: LpsVectors): number {
    const anterior = vectors.posterior.clone().negate();
    return angleFromPlane(acetabularVector, anterior);
}

/**
 * Create a direction unit-vector at the given radiographic-anteversion and -inclination
 * relative to the given anatomic axes.
 *
 * Definition of Radiographic angles (Murray 1992)
 * - The Radiographic Inclination (RI) is defined as the angle between the longitudinal axis and the
 *     acetabular axis when this is projected onto the coronal plane.
 * - The Radiographic Anteversion (RA) is defined as the angle between the acetabular axis and the
 *     coronal plane
 *
 * To generate the vector corresponding to a pair of radiographic angles:
 * 1. Vector initially points straight down (out of cup)
 * 2. Rotate vector about the AP axis by inclination degrees.
 * 3. Rotate the RL axis about the AP axis by inclination degrees.
 * 4. Rotate the vector about the rotated RL axis by anteversion degrees.
 *
 * @param anteversion - Radiographic anteversion in degrees.
 * @param inclination - Radiographic inclination in degrees.
 * @param axes - LPS coordinate system defining the reference frame.
 */
export function makeRadiographicDirection(
    anteversion: number,
    inclination: number,
    axes: LpsCoordinateSystem,
): Vector3 {
    const avRad = -degToRad(anteversion);
    const incRad = degToRad(inclination);

    // 1. Initial vector points inferior
    const normal = axes.inferior.clone();

    // 2. Rotate vector about AP axis by inclination
    normal.applyAxisAngle(axes.anterior, incRad);

    // 3. Rotate RL axis about AP axis — RL axis stays in the coronal plane
    const rightLeftAxisAbducted = axes.left.clone().applyAxisAngle(axes.anterior, incRad);

    // 4. Rotate vector about rotated RL axis — rotates normal out of the coronal plane
    return normal.applyAxisAngle(rightLeftAxisAbducted, avRad).normalize();
}

/**
 * Calculate radiographic inclination angle from an 'acetabular vector'. This is the angle between the
 * 'acetabular vector' and the inferior axis as projected in the coronal plane.
 * For both cups and acetabular planes the 'acetabular vector' points laterally (away from the pelvis).
 *
 * Definition of Radiographic angles (Murray 1992):
 * The Radiographic Inclination (RI) is defined as the angle between the longitudinal axis and the acetabular
 *      axis when this is projected onto the coronal plane.
 */
export function radiographicInclination(acetabularVector: Vector3, vecs: LpsVectors): number {
    const transverse = areAcute(acetabularVector, vecs.left)
        ? vecs.left
        : vecs.left.clone().negate();
    const inferior = vecs.superior.clone().negate();
    return angleInPlanarProjection(acetabularVector, inferior, transverse, 90);
}

/**
 * Convert radiographic angles from one coordinate system to another.
 *
 * Constructs a 3D cup-axis direction vector in the source CS,
 * then decomposes it into radiographic angles in the target CS.
 *
 * @param anteversion - Radiographic anteversion in degrees (source CS).
 * @param inclination - Radiographic inclination in degrees (source CS).
 * @param sourceCS - The coordinate system the input angles are expressed in.
 * @param targetCS - The coordinate system to express the output angles in.
 * @returns Radiographic anteversion and inclination in degrees (target CS).
 */
export function convertRadiographicAngles(
    anteversion: number,
    inclination: number,
    sourceCS: LpsCoordinateSystem,
    targetCS: LpsCoordinateSystem,
): { anteversion: number; inclination: number } {
    const vector = makeRadiographicDirection(anteversion, inclination, sourceCS);
    const targetLps = targetCS.toLpsVectors();
    return {
        anteversion: radiographicAnteversion(vector, targetLps),
        inclination: radiographicInclination(vector, targetLps),
    };
}

/** The radiographic anteversion/inclination of an anatomic cup rotation, in degrees */
export function getCupRotationInRadiographicDegrees(
    cupRotation: AcetabularAngles<'anatomic'>,
): AnteversionInclination {
    return toDegrees(toRadiographic(cupRotation));
}

/**
 * Apply a partial radiographic angle update (in degrees) to an anatomic cup rotation.
 * Returns the updated anatomic rotation.
 */
export function applyPartialRotationInRadiographicDegrees(
    currentRotation: AcetabularAngles<'anatomic'>,
    angles: Partial<AnteversionInclination>,
): AcetabularAngles<'anatomic'> {
    const currentRadiographic = getCupRotationInRadiographicDegrees(currentRotation);
    const newRadiographicAngles = radiographicAngles({ ...currentRadiographic, ...angles });
    return toAnatomic(toRadians(newRadiographicAngles));
}
